import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { userRequest } from '../utils/requestMethods';
import { getPriceForTerm, formatCurrency } from '../utils/currencyUtils';
import { Header, PaymentSummary } from '../components';
import { useStateContext } from '../contexts/ContextProvider';
import { FiArrowRight, FiBookOpen, FiSearch } from 'react-icons/fi';
import SkeletonTable from '../components/common/SkeletonTable';
import { generateSchoolSessions } from '../utils/schoolYearUtils';

const levelColors = {
  primary:   { bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200'  },
  secondary: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200'},
  nursery:   { bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200' },
};

const TERM_LABELS = { 1: 'Term 1', 2: 'Term 2', 3: 'Term 3' };
const schoolSessions = generateSchoolSessions(2020, 15);

const ClassPayments = () => {
  const { school } = useStateContext();
  const [classes, setClasses] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState();
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  const schoolId = school?._id;
  const schoolCode = school?.code;
  const currentTerm = school?.settings?.current_term;
  const currentSession = school?.settings?.current_session;
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);

  // Resolve the initial term/session once school data loads, then fetch
  // immediately. After that, changing the selects only updates local state —
  // fetching again requires clicking Apply.
  useEffect(() => {
    if (!currentTerm || !currentSession || !schoolCode || !schoolId) return;
    const t = Number(localStorage.getItem('classPayments_term')) || currentTerm;
    const s = localStorage.getItem('classPayments_session') || currentSession;
    setSelectedTerm(t);
    setSelectedSession(s);
    fetchData(t, s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTerm, currentSession, schoolCode, schoolId]);

  const fetchData = async (term, session) => {
    setLoading(true);
    try {
      const [classesRes, summaryRes] = await Promise.all([
        userRequest.get(`classes?code=${schoolCode}`),
        userRequest.get(`schools/${schoolId}/payment-summary?term=${term}&session=${encodeURIComponent(session)}`),
      ]);
      setClasses(classesRes.data.classes || []);
      setPaymentSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const submitHandler = (e) => {
    e.preventDefault();
    fetchData(selectedTerm, selectedSession);
  };

  const resetToCurrentTerm = () => {
    const t = currentTerm;
    const s = currentSession;
    setSelectedTerm(t);
    setSelectedSession(s);
    localStorage.setItem('classPayments_term', t);
    localStorage.setItem('classPayments_session', s);
    fetchData(t, s);
  };

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg min-h-screen">
      <Header category="Payments" title="Payment Per Class" />

      {/* Inline filter bar */}
      <form
        onSubmit={submitHandler}
        className="flex flex-wrap items-end gap-3 mb-6 p-4 rounded-2xl border border-gray-200 bg-gray-50"
      >
        <div className="flex-1 min-w-[100px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">Term</label>
          <select
            value={selectedTerm || ''}
            onChange={(e) => {
              const t = Number(e.target.value);
              setSelectedTerm(t);
              localStorage.setItem('classPayments_term', t);
            }}
            className="w-full rounded-xl bg-white border border-gray-200 text-sm px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="1">Term 1</option>
            <option value="2">Term 2</option>
            <option value="3">Term 3</option>
          </select>
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">Session</label>
          <select
            value={selectedSession || ''}
            onChange={(e) => {
              setSelectedSession(e.target.value);
              localStorage.setItem('classPayments_session', e.target.value);
            }}
            className="w-full rounded-xl bg-white border border-gray-200 text-sm px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            {schoolSessions.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={resetToCurrentTerm}
          disabled={loading}
          className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-100 rounded-full disabled:opacity-50 transition-colors"
        >
          Reset to Current Term
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-full disabled:bg-indigo-300 transition-colors"
        >
          {loading ? 'Loading...' : 'Apply'}
        </button>
      </form>

      <PaymentSummary paymentSummary={paymentSummary} />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-sm text-gray-500">
          {classes.length > 0 && (
            filterText
              ? `${classes.filter(c => c.class_name?.toLowerCase().includes(filterText.toLowerCase())).length} of ${classes.length} classes`
              : `${classes.length} class${classes.length !== 1 ? 'es' : ''}`
          )}
        </p>
        <div className="relative mb-8">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          <input
            type="text"
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            placeholder="Search classes..."
            className="pl-9 pr-4 py-2 text-sm rounded-lg bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 w-48 md:w-64"
          />
        </div>
      </div>

      {/* Class cards */}
      {loading ? (
        <SkeletonTable rows={5} columns={4} />
      ) : classes.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">No classes found.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {classes.filter(cls =>
            cls.class_name?.toLowerCase().includes(filterText.toLowerCase())
          ).map((cls) => {
            const level = cls.level_of_education?.toLowerCase();
            const colors = levelColors[level] || levelColors.primary;
            const fee = formatCurrency(getPriceForTerm(cls.class_price, selectedTerm));

            return (
              <div
                key={cls._id}
                className="rounded-2xl border border-gray-200 bg-white p-5 flex flex-col gap-4 hover:shadow-md transition-shadow"
              >
                {/* Top row */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl ${colors.bg}`}>
                      <FiBookOpen className={`text-lg ${colors.text}`} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 capitalize">{cls.class_name}</p>
                      <span className={`inline-block mt-0.5 text-xs font-medium px-2 py-0.5 rounded-full capitalize ${colors.bg} ${colors.text} border ${colors.border}`}>
                        {cls.level_of_education}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400">{TERM_LABELS[selectedTerm]} Fee</p>
                    <p className="text-sm font-bold text-indigo-600">{fee}</p>
                  </div>
                </div>

                {/* Action */}
                <Link to={`/class-transaction/${cls._id}/${encodeURIComponent(cls.class_name)}`}>
                  <button className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-xl transition-colors">
                    View Payments <FiArrowRight className="text-sm" />
                  </button>
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClassPayments;
